import React from "react";

/**
 * App-wide route error boundary.
 *
 * Why this exists: the app had NO error boundary anywhere, so any uncaught
 * error thrown while rendering a page (e.g. a bad data shape hitting a
 * `.map`/`.replace`, or a component-library edge case) caused React to unmount
 * the entire tree — the user saw a blank white screen with no indication of
 * what happened. This boundary contains such failures to the current page:
 * instead of a blank app it renders a readable panel with the error text, so
 * the failure is diagnosable and the rest of the shell (nav, other routes)
 * stays usable.
 *
 * Deliberately built with plain React + inline styles (no MUI/ag-grid/etc.) so
 * the fallback can never be broken by the same dependency that broke the page
 * it is catching for.
 *
 * Reset behaviour: the boundary is keyed by route path at the call site
 * (RouteWithSubRoutes), so navigating to a different route remounts it and
 * clears the error automatically.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface the full error + component stack to the console for diagnosis.
    // eslint-disable-next-line no-console
    console.error("Unhandled render error caught by ErrorBoundary:", error, info);
    this.setState({ info });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { error, info } = this.state;
    const message = (error && (error.message || String(error))) || "Unknown error";
    const stack = (info && info.componentStack) || (error && error.stack) || "";

    return (
      <div style={styles.wrap}>
        <div style={styles.card}>
          <div style={styles.title}>Something went wrong on this page</div>
          <div style={styles.subtitle}>
            The rest of the app is still working — you can go back or reload. If this keeps
            happening, share the details below with the team.
          </div>
          <div style={styles.errorLine}>{message}</div>
          {stack ? (
            <details style={styles.details}>
              <summary style={styles.summary}>Technical details</summary>
              <pre style={styles.pre}>{stack}</pre>
            </details>
          ) : null}
          <div style={styles.actions}>
            <button style={styles.button} onClick={this.handleReload}>
              Reload page
            </button>
          </div>
        </div>
      </div>
    );
  }
}

const styles = {
  wrap: { display: "flex", justifyContent: "center", padding: "48px 16px" },
  card: {
    maxWidth: 720,
    width: "100%",
    border: "1px solid #e0e0e0",
    borderLeft: "4px solid #d32f2f",
    borderRadius: 8,
    padding: "20px 24px",
    background: "#fff",
    fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
  },
  title: { fontSize: 18, fontWeight: 700, color: "#b71c1c", marginBottom: 6 },
  subtitle: { fontSize: 14, color: "#555", marginBottom: 14, lineHeight: 1.5 },
  errorLine: {
    fontFamily: "monospace",
    fontSize: 13,
    background: "#fdecea",
    color: "#611a15",
    padding: "10px 12px",
    borderRadius: 4,
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
  },
  details: { marginTop: 12 },
  summary: { cursor: "pointer", fontSize: 13, color: "#555", userSelect: "none" },
  pre: {
    marginTop: 8,
    maxHeight: 260,
    overflow: "auto",
    fontFamily: "monospace",
    fontSize: 12,
    background: "#f5f5f5",
    border: "1px solid #e0e0e0",
    borderRadius: 4,
    padding: 12,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  actions: { marginTop: 16, display: "flex", gap: 8 },
  button: {
    background: "#1976d2",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    padding: "8px 16px",
    fontSize: 14,
    cursor: "pointer",
  },
};

export default ErrorBoundary;
